import { useQuery } from '@tanstack/react-query';
import { gsap } from 'gsap';
import MotionPathPlugin from 'gsap/MotionPathPlugin';
import React, { useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import { Icon, Text } from '@column/column-ui-kit';
import { Card, CardDescription, CardHeader, CardTitle, cn } from '@column/ui';

import { ROUTE as APP_ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { SetupDeviceMFA } from '~/lib/authn';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { isWebAuthnSupported } from '~/lib/webauthn';
import { EmailState, UserRepository } from '~/repositories/UserRepository';
import { useSessionStore } from '~/stores/Session';
import { Flex, Headline } from '~/styles';

import { HintLink, Logo, Panel, animateLogo } from './_components';
import { SetupPasskeyFlow, type SetupPasskeyFlowHandle } from './_components/SetupPasskeyFlow';

const AdditionalLogo = styled(Logo)`
  margin-left: -12px;
  transform-origin: 0 0;
  overflow: visible;

  & > path {
    --logo-path-opacity: 0;
    --logo-path-x: 16px;
    --logo-path-y: 24px;
    opacity: var(--logo-path-opacity);
    transform: translate(var(--logo-path-x), var(--logo-path-y)) translateZ(0);
  }
`;

const HiddenLogo = styled(Logo)`
  opacity: 0;
`;

const LogoWrapper = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
`;

const MFAContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const MFAInner = styled.div`
  width: 100%;
  max-width: 600px;
`;

const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
  margin-bottom: 12px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const MethodCardButton = styled.button<{ $selected?: boolean }>`
  display: block;
  height: 100%;
  width: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;

  [data-slot='card'] {
    box-shadow:
      0 -1px 1px rgba(255, 255, 255, 0) inset,
      ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
        ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
        ${({ theme }) => theme.style.buttonSecondaryBorderColor},
      0 1px 1px rgba(0, 0, 0, 0.1),
      0 1px 4px 0 transparent inset;
  }

  ${({ $selected }) =>
    $selected &&
    css`
      [data-slot='card'] {
        background-color: ${({ theme }) =>
          theme.id !== 'dark' ? theme.background : theme.secondary.blendToBackground(25)};
        box-shadow:
          ${({ theme }) => theme.style.buttonSecondaryBorderProperties} 1.5px ${({ theme }) => theme.primary.background},
          0 0 0 var(--button-outline, 0px)
            var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor}),
          ${({ theme }) => theme.style.buttonSecondaryShadow};
      }
    `}

  ${({ $selected }) =>
    !$selected &&
    css`
      &:hover:not(:disabled) [data-slot='card'] {
        background-color: ${({ theme }) =>
          theme.id !== 'dark' ? theme.secondary.blendToBackground(50) : theme.secondary.blendToBackground(25)};
        box-shadow:
          0 -1px 1px rgba(255, 255, 255, ${({ theme }) => (theme.id !== 'dark' ? '.75' : '.075')}) inset,
          ${({ theme }) => theme.style.buttonSecondaryHoveredBorderProperties}
            ${({ theme }) => theme.style.buttonSecondaryHoveredBorderWidth}
            ${({ theme }) => theme.secondary.blendToBackground(1100, 150)},
          0 1px 4px 0 ${({ theme }) => theme.secondary.blendToBackground(120)} inset;
      }
    `}

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.style.buttonFocussedOutlineColor};
    outline-offset: 2px;
    border-radius: 8px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const ContinueWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 24px;
`;

const TitleWrapper = styled.div`
  padding-bottom: 16px;
`;

type MFASetupMethod = 'authenticator' | 'passkey';

const MFA_SUCCESS_REDIRECT_DELAY_MS = 300;

export const PageSetupMFA: React.FC = () => {
  const logoWrapperRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<SVGSVGElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const passkeyCardRef = useRef<HTMLButtonElement>(null);
  const passkeyFlowRef = useRef<SetupPasskeyFlowHandle>(null);
  const navigate = useNavigate();
  const { refetchCurrentUser } = useSessionStore();
  const passkeyAuthEnabled = useFeatureFlag(FeatureFlag.EnablePasskeyAuth);
  const passkeySupported = isWebAuthnSupported();
  const [setupMethod, setSetupMethod] = useState<MFASetupMethod>('authenticator');

  const handleSelectPasskey = () => {
    setSetupMethod('passkey');
    passkeyFlowRef.current?.start();
    requestAnimationFrame(() => passkeyCardRef.current?.focus());
  };

  const handleSelectAuthenticator = () => {
    passkeyFlowRef.current?.reset();
    setSetupMethod('authenticator');
  };

  const { data: userData, isPending: isUserPending } = useQuery({
    queryKey: ['user', 'self'],
    queryFn: () => UserRepository.fetchSelf(),
    retry: 1,
  });

  const freezeSetupPanel = () => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const panelRect = panel.getBoundingClientRect();
    panel.style.width = `${panelRect.width}px`;
    panel.style.height = `${panelRect.height}px`;
    panel.style.overflow = 'hidden';
    panel.style.pointerEvents = 'none';
  };

  const handleRedirectToDashboard = (refetchUser = true) => {
    if (!logoRef.current || !logoWrapperRef.current || !panelRef.current) {
      if (refetchUser) {
        void refetchCurrentUser();
      }
      navigate(APP_ROUTE.ROOT, {
        state: { from: 'auth' },
      });
      return;
    }

    freezeSetupPanel();

    if (refetchUser) {
      void refetchCurrentUser();
    }

    animateLogo(logoRef.current, logoWrapperRef.current, panelRef.current, gsap, MotionPathPlugin, () => {
      navigate(APP_ROUTE.ROOT, {
        state: { from: 'auth' },
      });
    });
  };

  const handleMFASetupComplete = () => {
    freezeSetupPanel();
    void refetchCurrentUser().finally(() => {
      window.setTimeout(() => handleRedirectToDashboard(false), MFA_SUCCESS_REDIRECT_DELAY_MS);
    });
  };

  return (
    <>
      <LogoWrapper ref={logoWrapperRef}>
        <AdditionalLogo size="24px" variant="full" ref={logoRef} />
      </LogoWrapper>
      <Panel ref={panelRef}>
        <HiddenLogo size="24px" />
        <Flex flexDirection="column" gap={8} mb={8}>
          <TitleWrapper>
            <Headline fullWidth size="small">
              Setup Multi-Factor Authentication
            </Headline>
          </TitleWrapper>
          <Loading isLoading={isUserPending}>
            <>
              {userData && userData.emailState !== EmailState.VERIFIED && (
                <Text size={14} color="foreground" weight="normal">
                  Please verify your email address to setup multi-factor authentication.
                </Text>
              )}
              {userData && userData.emailState === EmailState.VERIFIED && (
                <MFAContainer>
                  <MFAInner>
                    {passkeyAuthEnabled && (
                      <MethodGrid>
                        <MethodCardButton
                          type="button"
                          aria-pressed={setupMethod === 'authenticator'}
                          $selected={setupMethod === 'authenticator'}
                          onClick={handleSelectAuthenticator}
                        >
                          <Card
                            size="sm"
                            className="h-full min-h-24 py-3 transition-[box-shadow,background-color] duration-200"
                          >
                            <CardHeader className="gap-1">
                              <div
                                className={cn(
                                  'mb-1 text-muted-foreground transition-colors [--icon-size:20px]',
                                  setupMethod === 'authenticator' && 'text-primary'
                                )}
                              >
                                <Icon.Phone />
                              </div>
                              <CardTitle className="text-sm">Authenticator app</CardTitle>
                              <CardDescription className="text-xs leading-4">
                                Generate one-time verification codes with your preferred authenticator app.
                              </CardDescription>
                            </CardHeader>
                          </Card>
                        </MethodCardButton>
                        <MethodCardButton
                          ref={passkeyCardRef}
                          type="button"
                          aria-pressed={setupMethod === 'passkey'}
                          $selected={setupMethod === 'passkey'}
                          disabled={!passkeySupported}
                          onClick={handleSelectPasskey}
                        >
                          <Card
                            size="sm"
                            className="h-full min-h-24 py-3 transition-[box-shadow,background-color] duration-200"
                          >
                            <CardHeader className="gap-1">
                              <div
                                className={cn(
                                  'mb-1 text-muted-foreground transition-colors [--icon-size:20px]',
                                  setupMethod === 'passkey' && 'text-primary'
                                )}
                              >
                                <Icon.Key />
                              </div>
                              <CardTitle className="text-sm">Passkey</CardTitle>
                              <CardDescription className="text-xs leading-4">
                                {passkeySupported
                                  ? 'Sign in to your account with secure, one-click authentication.'
                                  : 'Passkeys are not supported by this browser.'}
                              </CardDescription>
                            </CardHeader>
                          </Card>
                        </MethodCardButton>
                      </MethodGrid>
                    )}
                    {!passkeyAuthEnabled && (
                      <SetupDeviceMFA
                        enabled
                        onSetupComplete={handleMFASetupComplete}
                        errorNotificationDisplay="global"
                      />
                    )}
                    {passkeyAuthEnabled && setupMethod === 'authenticator' && (
                      <SetupDeviceMFA
                        enabled
                        fullWidth
                        onSetupComplete={handleMFASetupComplete}
                        errorNotificationDisplay="global"
                      />
                    )}
                    {passkeyAuthEnabled && (
                      <SetupPasskeyFlow
                        ref={passkeyFlowRef}
                        notificationDisplay="global"
                        onFailure={() => setSetupMethod('authenticator')}
                        onSuccess={handleMFASetupComplete}
                      />
                    )}
                  </MFAInner>
                </MFAContainer>
              )}
              <ContinueWrapper>
                <HintLink onClick={() => handleRedirectToDashboard()}>Continue to dashboard</HintLink>
              </ContinueWrapper>
            </>
          </Loading>
        </Flex>
      </Panel>
    </>
  );
};
